import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Loader2, AlertTriangle, Shield, ShieldAlert, ShieldCheck, Filter } from 'lucide-react';

const ALLOCATION_OPTIONS = [
  { value: 'unassigned',   label: '— Unassigned —' },
  { value: 'cohort_1',     label: 'Cohort 1' },
  { value: 'cohort_2',     label: 'Cohort 2' },
  { value: 'cohort_3',     label: 'Cohort 3' },
  { value: 'on_hold',      label: 'On Hold' },
  { value: 'not_selected', label: 'Not Selected' },
];

const INTEGRITY_CONFIG = {
  clean:        { label: 'Clean',      color: 'text-green-600',  bg: 'bg-green-50',  icon: ShieldCheck },
  watch:        { label: 'Watch',      color: 'text-amber-600',  bg: 'bg-amber-50',  icon: Shield },
  suspicious:   { label: 'Suspicious', color: 'text-orange-600', bg: 'bg-orange-50', icon: ShieldAlert },
  flagged:      { label: 'Flagged',    color: 'text-red-600',    bg: 'bg-red-50',    icon: ShieldAlert },
  disqualified: { label: 'Disqualified', color: 'text-gray-400', bg: 'bg-gray-50',   icon: ShieldAlert },
  none:         { label: '',           color: '',                 bg: '',             icon: null },
  unknown:      { label: '',           color: '',                 bg: '',             icon: null },
};

function getAllocationValue(app) {
  if (app.status === 'accepted') return `cohort_${app.cohort_number}`;
  if (app.status === 'on_hold') return 'on_hold';
  if (app.status === 'not_selected') return 'not_selected';
  return 'unassigned';
}

function parseAllocationValue(val) {
  if (val === 'unassigned') return { status: 'test_completed', cohort_number: null };
  if (val === 'on_hold') return { status: 'on_hold', cohort_number: null };
  if (val === 'not_selected') return { status: 'not_selected', cohort_number: null };
  const n = parseInt(val.replace('cohort_', ''));
  return { status: 'accepted', cohort_number: n };
}

export default function FinalEvaluationPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();

  const [intake, setIntake] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [filterIntegrity, setFilterIntegrity] = useState('all');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const [intakeRes, appsRes] = await Promise.all([
        axios.get(`${API}/intake-periods/${intakeId}`, auth()),
        axios.get(`${API}/applications?intake_id=${intakeId}`, auth()),
      ]);
      setIntake(intakeRes.data);
      // Only show test_completed, accepted, on_hold, not_selected
      const relevant = appsRes.data.filter(a =>
        ['test_completed', 'accepted', 'on_hold', 'not_selected'].includes(a.status)
      );
      setApps(relevant);
    } catch {
      toast.error('Failed to load evaluation data');
    } finally {
      setLoading(false);
    }
  }, [intakeId]);

  useEffect(() => { load(); }, [load]);

  const updateAllocation = async (appId, value) => {
    const { status, cohort_number } = parseAllocationValue(value);
    setSaving(s => ({ ...s, [appId]: true }));
    try {
      await axios.put(`${API}/applications/${appId}/allocate`, { status, cohort_number }, auth());
      setApps(prev => prev.map(a =>
        a.id === appId ? { ...a, status, cohort_number } : a
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save allocation');
    } finally {
      setSaving(s => ({ ...s, [appId]: false }));
    }
  };

  // ── Cohort counters ────────────────────────────────────────────────────────
  const counts = apps.reduce((acc, a) => {
    const val = getAllocationValue(a);
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {});

  // ── Score distribution for histogram ──────────────────────────────────────
  const scoresBuckets = Array.from({ length: 10 }, (_, i) => ({
    range: `${i * 10}–${i * 10 + 10}`,
    count: apps.filter(a => {
      const s = a.final_score ?? a.aptitude_score ?? 0;
      return s >= i * 10 && s < (i + 1) * 10;
    }).length,
  }));
  const maxCount = Math.max(...scoresBuckets.map(b => b.count), 1);

  // ── Filtered apps ──────────────────────────────────────────────────────────
  const filteredApps = filterIntegrity === 'all'
    ? apps
    : apps.filter(a => {
        const level = a.test_disqualified ? 'disqualified' : (a.integrity_level || 'clean');
        if (filterIntegrity === 'flagged') return ['flagged', 'suspicious', 'watch'].includes(level);
        return level === filterIntegrity;
      });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  const flaggedCount = apps.filter(a => ['flagged', 'suspicious'].includes(a.integrity_level) && !a.test_disqualified).length;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/intakes/${intakeId}`)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-primary">Final Evaluation</h1>
          <p className="text-sm text-muted-foreground">{intake?.name}</p>
        </div>
      </div>

      {/* ── Cohort Counters ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { val: 'cohort_1', label: 'Cohort 1', color: 'blue' },
          { val: 'cohort_2', label: 'Cohort 2', color: 'indigo' },
          { val: 'cohort_3', label: 'Cohort 3', color: 'purple' },
          { val: 'on_hold',  label: 'On Hold',  color: 'amber' },
          { val: 'unassigned', label: 'Unassigned', color: 'gray' },
        ].map(({ val, label, color }) => (
          <div key={val} className={`rounded-xl border p-3 text-center ${
            color === 'blue' ? 'border-blue-200 bg-blue-50' :
            color === 'indigo' ? 'border-indigo-200 bg-indigo-50' :
            color === 'purple' ? 'border-purple-200 bg-purple-50' :
            color === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
          }`}>
            <div className={`text-2xl font-bold ${
              color === 'blue' ? 'text-blue-700' :
              color === 'indigo' ? 'text-indigo-700' :
              color === 'purple' ? 'text-purple-700' :
              color === 'amber' ? 'text-amber-700' : 'text-gray-500'
            }`}>{counts[val] || 0}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Score Distribution Chart ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Final Score Distribution ({apps.length} candidates)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-24">
            {scoresBuckets.map((b) => (
              <div key={b.range} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-muted-foreground">{b.count || ''}</span>
                <div
                  className="w-full bg-primary/70 rounded-t"
                  style={{ height: `${(b.count / maxCount) * 72}px`, minHeight: b.count > 0 ? 4 : 0 }}
                />
                <span className="text-[9px] text-muted-foreground leading-none">{b.range}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Integrity Warning ── */}
      {flaggedCount > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">{flaggedCount} applicant{flaggedCount > 1 ? 's' : ''} with integrity flags</p>
            <p className="text-xs text-amber-700 mt-0.5">Review their Test Activity tab before finalising allocation. You can still allocate flagged applicants.</p>
          </div>
        </div>
      )}

      {/* ── Allocation Table ── */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Allocation Table — {apps.length} candidates</CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterIntegrity} onValueChange={setFilterIntegrity}>
              <SelectTrigger className="h-8 w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All candidates</SelectItem>
                <SelectItem value="flagged">Flagged / Suspicious</SelectItem>
                <SelectItem value="clean">Clean only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground w-12">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Applicant</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">App Score</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Test Score</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Final Score</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Integrity</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground w-44">Allocate</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((app, i) => {
                  const integrityLevel = app.test_disqualified ? 'disqualified' : (app.integrity_level || 'clean');
                  const ic = INTEGRITY_CONFIG[integrityLevel] || INTEGRITY_CONFIG.clean;
                  const IntegrityIcon = ic.icon;
                  const isFlagged = ['flagged', 'suspicious'].includes(integrityLevel);

                  return (
                    <tr key={app.id} className={`border-b hover:bg-gray-50/50 transition-colors ${isFlagged ? 'bg-orange-50/30' : ''}`}>
                      <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{i + 1}</td>
                      <td className="px-4 py-3">
                        <button
                          className="text-left hover:underline font-medium text-primary"
                          onClick={() => navigate(`/admin/applications/${app.id}`)}
                        >
                          {app.full_name}
                        </button>
                        <div className="text-xs text-muted-foreground">{app.email}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">{app.score?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs">{app.aptitude_score?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm font-semibold">{app.final_score?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-3 text-center">
                        {IntegrityIcon && ic.label ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ic.bg} ${ic.color}`}>
                            <IntegrityIcon className="w-3 h-3" />
                            {ic.label}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {saving[app.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" />
                        ) : (
                          <Select
                            value={getAllocationValue(app)}
                            onValueChange={(val) => updateAllocation(app.id, val)}
                          >
                            <SelectTrigger className="h-7 text-xs w-36 mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ALLOCATION_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value} className="text-xs">
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredApps.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">No candidates match the current filter.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
